import { type CSSProperties } from 'react';
import { Card } from './card';
import { CheckCircle } from '@/components/pages/icons';
import type { CardItem, RecoverExpertiseRootProps } from './type';

function smoothstep(t: number): number {
  return t * t * (3 - 2 * t);
}

function getCardStyle(index: number, progress: number, total: number): CSSProperties {
  const reversedIdx = total - 1 - index;
  const turnLocal = Math.max(0, Math.min(1, progress - reversedIdx));
  const turnEased = smoothstep(turnLocal);

  // Stack offsets: top card (index=N-1) is furthest right and at y=0
  const stackOffsetX = index * 3; // % of own width
  const stackOffsetY = reversedIdx * 3; // px

  const flyX = turnEased * 140; // % — flies off to the right

  // Blur: cards beneath are blurred, clear up as cards above fly away
  let clearUp = 0;
  for (let above = 0; above < reversedIdx; above++) {
    const t = Math.max(0, Math.min(1, progress - above));
    clearUp += smoothstep(t);
  }
  const myBlur = Math.max(0, reversedIdx * total - clearUp * total);

  return {
    transform: `translate(${stackOffsetX + flyX}%, ${stackOffsetY}px)`,
    opacity: 1 - turnEased,
    filter: myBlur > 0.1 ? `blur(${myBlur.toFixed(2)}px)` : undefined,
    zIndex: 10 - reversedIdx,
    willChange: 'transform, opacity, filter',
  };
}

export function RecoverExpertiseServer({
  containerRef,
  stickyContainerRef,
  children,
  title = '',
  underline = '',
  compensations = [],
  cards,
  totalHeight = 0,
  progress = 0,
  activeIndex = 0,
  isMobile,
}: RecoverExpertiseRootProps) {
  const cardsSize = cards.length;

  return (
    <section
      id="about-us"
      className="relative"
      ref={containerRef}
      style={{ height: isMobile ? `${totalHeight}px` : undefined }}
    >
      <div
        ref={stickyContainerRef}
        className="w-full mx-auto max-w-page sticky top-0 @lg:relative @lg:pb-6 max-w-vw overflow-hidden @lg:h-full"
        data-wrapper
      >
        {children}

        <div className="flex flex-col justify-end @xs:gap-10 @md:px-8 @md:min-h-236" data-content>
          <div className="flex flex-col @md:px-[15%] z-10 text-brand-text-button pt-3 @xs:pt-8 @md:pt-0">
            <h2
              className="section-title @xs:mb-3 @md:mb-8 text-left px-8 @md:px-0 [&_i]:section-subtitle"
              dangerouslySetInnerHTML={{
                __html: title,
              }}
            ></h2>

            <p
              className="text-base leading-normal mb-2 @md:leading-6 @md:mb-3 px-8 @md:px-0"
              dangerouslySetInnerHTML={{
                __html: underline,
              }}
            ></p>

            <ul className="flex flex-col gap-2 px-8 @md:px-0">
              {compensations.map(({ label }, idx) => (
                <li key={`compensation-type-${idx}`} className="flex items-center gap-1.5">
                  <CheckCircle className="w-7 h-7 text-[#10b981]" />
                  <span className="list-item-serif text-[#172554] whitespace-nowrap italic">
                    {label}
                  </span>
                </li>
              ))}
            </ul>
          </div>

          <div className="relative @lg:hidden">
            <div className="flex flex-col justify-center">
              <div className="relative ml-6" style={{ height: 370 }}>
                {[...cards].reverse().map((card, i) => (
                  <Card
                    key={`recover-made-card-${card.background_image}`}
                    item={card}
                    style={
                      isMobile && activeIndex === cardsSize - 1 && progress < 100 && i === 0
                        ? { zIndex: 1 }
                        : getCardStyle(i, progress, cards.length)
                    }
                  />
                ))}
              </div>
            </div>
          </div>
          <div className="hidden @lg:grid grid-cols-3 gap-6 w-full z-10">
            {cards.map((card: CardItem) => (
              <Card key={`desktop-recover-made-card-${card.background_image}`} item={card} />
            ))}
          </div>
        </div>

        <div
          className="absolute z-0 left-4 right-4 top-0 @md:top-15.5 bottom-0 @md:right-[10%] @md:left-[10%]  rounded-2xl bg-[linear-gradient(333.34deg,rgba(239,246,255,0)_42.56%,rgba(186,216,253,0.7)_88.23%)]"
          data-cover
        />
      </div>
    </section>
  );
}
